import React, { useMemo, useRef, useState, useEffect } from "react";
import NodeCircle from "./NodeCircle";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// MyTeamView — universal default landing. Renders the immediate
// relational world of the focused user across 4 rows:
//   Row 1: Leader (parent — God for Alpha members)
//   Row 2: 3 Peers (user + up to 2 peers from parent.childNodeIds)
//   Row 3: 3 direct reports (user.childNodeIds)
//   Row 4: 9 grand-reports (user.children[*].childNodeIds)
// SVG connectors are drawn from measured DOM positions.
// =============================================================

function padTo(existing = [], count, parent, levelOffset = 1) {
  const arr = [...existing];
  const missing = Math.max(0, count - arr.length);
  for (let i = 0; i < missing; i++) {
    arr.push({
      nodeId: `__ph_${parent?.nodeId || "root"}_${levelOffset}_${arr.length}`,
      slotRef: null,
      level: (parent?.level ?? -1) + levelOffset,
      levelName: "",
      status: "Vacant",
      memberId: null,
      memberNumber: null,
      childNodeIds: [],
      _placeholder: true,
    });
  }
  return arr;
}

export default function MyTeamView({
  tree, focusNodeId, members, currentUser, healthMap = {}, onFocusNode, onOpenNode, onDrillDown,
}) {
  const nodes = tree?.nodes || {};
  const focus = focusNodeId ? nodes[focusNodeId] : null;
  const leader = focus?.parentNodeId ? nodes[focus.parentNodeId] : null;

  // Peers = focus.parent's childNodeIds (max 3). If focus itself is God (no leader),
  // peers = focus itself only (rendered center-large).
  const peers = useMemo(() => {
    if (!focus) return [];
    if (!leader) return [focus];
    const ids = leader.childNodeIds || [];
    const raw = ids.map(id => nodes[id]).filter(Boolean);
    // Ensure focus is centered — reorder so [left, focus, right].
    const others = raw.filter(n => n.nodeId !== focus.nodeId);
    const centered = [others[0], focus, others[1]].filter(Boolean);
    return padTo(centered, 3, leader, 1);
  }, [focus, leader, nodes]);

  // Direct reports of the focus.
  const reports = useMemo(() => {
    if (!focus) return [];
    const kids = (focus.childNodeIds || []).map(id => nodes[id]).filter(Boolean);
    return padTo(kids, 3, focus, 1);
  }, [focus, nodes]);

  // Grand-reports grouped under each report.
  const grandReports = useMemo(() => {
    return reports.map(r => {
      const kids = r._placeholder ? [] : (r.childNodeIds || []).map(id => nodes[id]).filter(Boolean);
      return { parent: r, children: padTo(kids, 3, r, 1) };
    });
  }, [reports, nodes]);

  const SIZE_LEADER = 70, SIZE_PEER = 68, SIZE_REPORT = 54, SIZE_GRAND = 38;

  const containerRef = useRef(null);
  const nodeRefs = useRef({});
  const [connectors, setConnectors] = useState([]);

  useEffect(() => {
    const compute = () => {
      const box = containerRef.current?.getBoundingClientRect();
      if (!box) return;
      const rect = (key) => {
        const el = nodeRefs.current[key];
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { cx: r.left - box.left + r.width / 2, top: r.top - box.top, bot: r.bottom - box.top };
      };
      const lines = [];
      // Leader → focus (center peer)
      const focusIdx = peers.findIndex(p => p.nodeId === focus?.nodeId);
      const lR = rect("leader");
      const focusPeerR = focusIdx >= 0 ? rect(`peer-${focusIdx}`) : null;
      if (lR && focusPeerR) {
        lines.push({ from: [lR.cx, lR.bot + 4], to: [focusPeerR.cx, focusPeerR.top - 4], strong: true });
      }
      // Focus → reports
      if (focusPeerR) {
        reports.forEach((_, i) => {
          const rR = rect(`report-${i}`);
          if (rR) lines.push({ from: [focusPeerR.cx, focusPeerR.bot + 4], to: [rR.cx, rR.top - 4] });
        });
      }
      // Reports → grand-reports
      grandReports.forEach((g, gi) => {
        const rR = rect(`report-${gi}`);
        if (!rR) return;
        g.children.forEach((_, ci) => {
          const cR = rect(`grand-${gi}-${ci}`);
          if (cR) lines.push({ from: [rR.cx, rR.bot + 4], to: [cR.cx, cR.top - 4], dim: true });
        });
      });
      setConnectors(lines);
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(compute));
    window.addEventListener("resize", compute);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", compute); };
  }, [focus, leader, peers, reports, grandReports]);

  const setRef = (key) => (el) => { if (el) nodeRefs.current[key] = el; };

  if (!focus) {
    return (
      <div data-testid="myteam-empty" style={{ padding: 24, textAlign: "center", color: "var(--kc-sub)" }}>
        No node to display.
      </div>
    );
  }

  const isFocusUser = currentUser?.memberId && focus.memberId === currentUser.memberId;

  return (
    <div data-testid="myteam-view" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 700 }}>
          {isFocusUser ? "My Team" : `Team of ${focus.levelName || "?"}${focus.slotRef ? ` · ${focus.slotRef}` : ""}`}
        </div>
        {onDrillDown && (
          <button
            className="btn btn-sm btn-outline"
            data-testid="myteam-drilldown-btn"
            onClick={() => onDrillDown(focus)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            Explore Deeper <CustomIcon name="arrow-right" size={11} />
          </button>
        )}
      </div>

      <div ref={containerRef} style={{ position: "relative", background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: "24px 16px", overflowX: "auto" }}>
        <svg style={{ position: "absolute", inset: 0, pointerEvents: "none", width: "100%", height: "100%" }} data-testid="myteam-svg">
          {connectors.map((c, i) => {
            const midY = (c.from[1] + c.to[1]) / 2;
            const d = `M ${c.from[0]} ${c.from[1]} L ${c.from[0]} ${midY} L ${c.to[0]} ${midY} L ${c.to[0]} ${c.to[1]}`;
            return <path key={i} d={d} stroke="var(--kc-accent)" strokeWidth={c.strong ? 2 : 1.5} fill="none" strokeOpacity={c.dim ? 0.35 : 0.75} />;
          })}
        </svg>

        <div style={{ minWidth: 560, display: "flex", flexDirection: "column", gap: 32, position: "relative" }}>
          {/* Row 1: Leader */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            {leader ? (
              <div ref={setRef("leader")} onClick={() => onOpenNode && onOpenNode(leader)} style={{ cursor: "pointer" }}>
                <NodeCircle
                  node={leader}
                  members={members}
                  size={SIZE_LEADER}
                  healthOverride={healthMap[leader.nodeId]?.health}
                  highlighted={false}
                  onClick={() => onOpenNode && onOpenNode(leader)}
                />
              </div>
            ) : (
              // Focus IS God — nothing above.
              <div style={{ height: SIZE_LEADER, display: "flex", alignItems: "center", color: "var(--kc-meta)", fontSize: 11, fontFamily: "'Cinzel',serif", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Eternal
              </div>
            )}
          </div>

          {/* Row 2: 3 Peers (or focus alone if root) */}
          <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", gap: 12 }}>
            {peers.map((p, i) => (
              <div key={p.nodeId} ref={setRef(`peer-${i}`)} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <NodeCircle
                  node={p}
                  members={members}
                  size={p.nodeId === focus.nodeId ? SIZE_PEER + 6 : SIZE_PEER}
                  healthOverride={healthMap[p.nodeId]?.health}
                  highlighted={currentUser?.memberId && p.memberId === currentUser.memberId}
                  onClick={() => {
                    if (p._placeholder) return;
                    if (p.isGodNode) { onOpenNode && onOpenNode(p); return; }
                    if (p.nodeId === focus.nodeId) { onDrillDown && onDrillDown(p); }
                    else { onFocusNode && onFocusNode(p.nodeId); }
                  }}
                />
              </div>
            ))}
          </div>

          {/* Row 3: 3 direct reports */}
          <div style={{ display: "flex", justifyContent: "space-around", gap: 12 }}>
            {reports.map((r, i) => (
              <div key={r.nodeId} ref={setRef(`report-${i}`)} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <NodeCircle
                  node={r}
                  members={members}
                  size={SIZE_REPORT}
                  healthOverride={healthMap[r.nodeId]?.health}
                  highlighted={currentUser?.memberId && r.memberId === currentUser.memberId}
                  onClick={() => !r._placeholder && onFocusNode && onFocusNode(r.nodeId)}
                />
              </div>
            ))}
          </div>

          {/* Row 4: 9 grand-reports grouped — scrolls horizontally on narrow viewports */}
          <div
            data-testid="myteam-gamma-row"
            className="myteam-gamma-scroll"
            style={{
              display: "flex", justifyContent: "space-around", gap: 24,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              // subtle scroll shadow on the right edge
              maskImage: "linear-gradient(90deg, #000 92%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(90deg, #000 92%, transparent 100%)",
            }}
          >
            <style>{`.myteam-gamma-scroll::-webkit-scrollbar{height:4px}.myteam-gamma-scroll::-webkit-scrollbar-thumb{background:color-mix(in srgb, var(--kc-accent) 35%, transparent);border-radius:4px}`}</style>
            {grandReports.map((g, gi) => (
              <div key={g.parent.nodeId + "-grp"} style={{ flex: "1 0 auto", minWidth: 160, display: "flex", justifyContent: "space-around", gap: 4 }}>
                {g.children.map((c, ci) => (
                  <div key={c.nodeId} ref={setRef(`grand-${gi}-${ci}`)} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    <NodeCircle
                      node={c}
                      members={members}
                      size={SIZE_GRAND}
                      healthOverride={healthMap[c.nodeId]?.health}
                      highlighted={currentUser?.memberId && c.memberId === currentUser.memberId}
                      onClick={() => !c._placeholder && onOpenNode && onOpenNode(c)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
        <div data-testid="myteam-summary">
          {reports.filter(r => !r._placeholder && r.memberId).length} direct · {grandReports.reduce((a, g) => a + g.children.filter(c => !c._placeholder && c.memberId).length, 0)} grand-reports
        </div>
      </div>
    </div>
  );
}
